import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCreatorStore } from '../stores/creatorStore'
import { useToastStore } from '../stores/toastStore'

export default function DraftView() {
  const { collectionId } = useParams<{ collectionId: string }>()
  const navigate = useNavigate()
  const { currentCollection, loading, error, fetchCollectionDetail } = useCreatorStore()

  const id = Number(collectionId)

  useEffect(() => {
    fetchCollectionDetail(id)
  }, [id, fetchCollectionDetail])

  // Poll while drafting
  useEffect(() => {
    if (!currentCollection || currentCollection.status !== 'drafting') return
    const interval = setInterval(() => {
      fetchCollectionDetail(id)
    }, 3000)
    return () => clearInterval(interval)
  }, [currentCollection, id, fetchCollectionDetail])

  if (loading && !currentCollection) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && !currentCollection) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <p className="text-error text-sm mb-4">{error}</p>
        <button
          onClick={() => fetchCollectionDetail(id)}
          className="px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!currentCollection) {
    return (
      <div className="p-4 text-center text-text-muted">Collection not found</div>
    )
  }

  const isDrafting = currentCollection.status === 'drafting'

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-surface border-b border-elevated shrink-0">
        <button
          onClick={() => navigate(`/creator/${id}`)}
          aria-label="Back to Collection"
          className="text-text-secondary hover:text-text-primary"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h1 className="font-medium truncate flex-1">
          Draft: {currentCollection.title}
        </h1>
      </div>

      {/* Draft content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isDrafting ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin mb-4" />
            <h2 className="text-lg font-semibold mb-1">Generating Draft</h2>
            <p className="text-text-muted">This may take a moment...</p>
          </div>
        ) : currentCollection.draft_content ? (
          <div className="prose prose-invert max-w-none">
            <div className="whitespace-pre-wrap text-text-primary leading-relaxed">
              {currentCollection.draft_content}
            </div>
          </div>
        ) : (
          <div className="text-center text-text-muted mt-8">
            No draft content yet.
          </div>
        )}
      </div>

      {/* Bottom actions */}
      {!isDrafting && (
        <div className="flex gap-3 px-4 py-3 bg-surface border-t border-elevated shrink-0">
          <button
            onClick={() => navigate(`/creator/${id}`)}
            className="flex-1 py-2.5 rounded-lg bg-elevated text-text-secondary font-medium hover:bg-bg transition-colors"
          >
            Back to Collection
          </button>
          {currentCollection.draft_content && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(currentCollection.draft_content!)
                useToastStore.getState().addToast('Copied!', 'success')
              }}
              className="py-2.5 px-4 rounded-lg bg-elevated text-text-secondary font-medium hover:bg-bg transition-colors"
            >
              Copy
            </button>
          )}
          {currentCollection.google_doc_url && (
            <a
              href={currentCollection.google_doc_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 rounded-lg bg-accent text-bg font-medium text-center hover:bg-accent-hover transition-colors"
            >
              Open in Google Docs
            </a>
          )}
        </div>
      )}
    </div>
  )
}
